import React, { ComponentProps, useId, useState } from 'react';

import { asFetchableBlock, useFocusOnRefresh, useRefreshBlock } from '../../_lib/BlockFetcher';
import { useDelayed } from '../../hooks/useDelayed';
import { BlockType } from '../../types/api/ElementType';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { EventParticipationDefaultView } from './views/EventParticipationDefaultView';
import { ParticipatingMembersView } from './views/ParticipatingMembersView';
import { BlockBar, BlockBarButton } from '../../_lib/components/BlockBar';
import { Icons } from '../../components/atoms/SVG';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { DefaultView, EditView, View, Views } from '../../types/BlockWithViews';
import { EventParticipationEditView } from './views/EventParticipationEditView';
import ModalDialog from '../../components/organisms/ModalDialog/ModalDialog';

type EventParticipationProps = Views<
  DefaultView<EventParticipationDefaultView> &
    EditView<EventParticipationEditView> &
    View<'participantListView', ParticipatingMembersView>
>;

export function EventParticipation(props: EventParticipationProps) {
  const { t } = useSonTranslation('EventParticipation');
  const { editModeInfo } = useAppContext();
  const { active: editModeActive } = editModeInfo;
  const { refresh, refreshCount } = useRefreshBlock();

  const [editFormVisible, setEditFormVisible] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const id = useId();
  // refreshcount is passed from blockfetch, increasing when fetch has been triggered.
  const editButtonRef = useFocusOnRefresh<HTMLButtonElement>(refreshCount);

  const onSuccess = useDelayed(() => {
    setEditFormVisible(false);
    refresh();
  });

  return (
    <>
      {editModeActive && (
        <BlockBar heading={t('blockEdit.title')} hierarchy={2}>
          <BlockBarButton
            onClick={() => setShowParticipants(true)}
            svgSpriteId={Icons.ANALYSIS}
            text={t('participantListView.button.showParticipants')}
          />
          <BlockBarButton
            text={t('blockEdit.editButtonLabel')}
            ref={editButtonRef}
            svgSpriteId={editFormVisible ? Icons.DECLINE_CROSS : Icons.EDIT_PENCIL}
            onClick={() => setEditFormVisible(prev => !prev)}
            aria-controls={id}
            aria-expanded={editFormVisible}
          />
        </BlockBar>
      )}
      {editFormVisible ? <EventParticipationEditView {...props.views.edit} onSuccess={onSuccess} /> : null}
      {showParticipants && (
        <ModalDialog closeModal={() => setShowParticipants(false)} headline={t('participantListView.modal.headline')}>
          <ParticipatingMembersView {...props.views.participantListView} />
        </ModalDialog>
      )}
      {!editModeActive ? <EventParticipationDefaultView {...props.views.default} /> : null}
    </>
  );
}

export const EventParticipationBlock = asFetchableBlock<EventParticipationProps, BlockType.eventparticipation>(
  EventParticipation
);
export type EventParticipationBlock = ComponentProps<typeof EventParticipationBlock>;
